import type { BigDecimal } from "@solomon-labs/bigdecimal";
import { CacheDuration } from "@solomon-labs/caching";
import { PublicKey } from "@solomon-labs/publickey";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import type { SolanaAddress } from "@solomon-labs/types";
import { useQuery } from "@tanstack/react-query";

import type { TokenBalances } from "../types/balances";
import { fetchSPLTokenAccountsFallback } from "../utils/balanceFetcher";
import { parseTokenBalances } from "../utils/parseTokenBalances";

export interface UseUsdvBalanceResult {
    /** USDv balance in decimal units. `null` while loading, on error, or when the wallet holds no USDv. */
    amount: BigDecimal | null;
    isLoading: boolean;
    isFetching: boolean;
    hasFetched: boolean;
    error: Error | null;
    refetch: () => void;
}

export const USDV_BALANCE_STALE_TIME_MS = 60 * 1000;
export const USDV_BALANCE_GC_TIME_MS = 5 * 60 * 1000;

/** Query key for a single address's USDv balance — shared between
 *  `useUsdvBalanceForAddress` and batch consumers (`useQueries`) so the
 *  per-address cache entry is fetched once. */
export function usdvBalanceQueryKey(address: string): readonly [string, string] {
    return ["usdv-balance", address] as const;
}

/** Query key for every token balance of a single address. */
export function tokenBalancesQueryKey(address: string): readonly [string, string] {
    return ["token-balances", address] as const;
}

async function fetchTokenAccountsForAddress(address: string): Promise<{ mint: string; amount: string }[]> {
    const owner = new PublicKey(address as SolanaAddress);
    try {
        // React Query owns freshness; its explicit refresh must reach the chain.
        const result = await ConnectionManager.getTokenAccountsForOwner(owner, { cacheDuration: CacheDuration.Bust });
        return result.allAccounts.map((a) => ({
            mint: a.mint.toString(),
            amount: a.amount.toString()
        }));
    } catch (error) {
        const isToken2022Error =
            error instanceof Error &&
            (error.message.includes("Token-2022") || error.constructor.name === "AccountDecodingError");
        if (!isToken2022Error) {
            console.warn(`fetchTokenAccountsForAddress(${address}): primary fetch failed`, error);
        }
        // Fall through to direct RPC for both supported token programs.
        return fetchSPLTokenAccountsFallback(address);
    }
}

/** Fetches every tracked token balance (USDv, SOLO, …) for an arbitrary Solana address. */
export async function fetchTokenBalancesForAddress(address: string): Promise<TokenBalances> {
    return parseTokenBalances(await fetchTokenAccountsForAddress(address));
}

/**
 * Fetches the USDv balance for an arbitrary Solana address. Exported for
 * batch consumers that need one query per address via `useQueries` (e.g.
 * portfolio totals) — pair with `usdvBalanceQueryKey` so the cache is
 * shared with `useUsdvBalanceForAddress`.
 */
export async function fetchUsdvBalanceForAddress(address: string): Promise<BigDecimal | null> {
    const accounts = await fetchTokenAccountsForAddress(address);
    const usdv = parseTokenBalances(accounts).usdv;
    if (usdv === null) {
        console.warn(
            `useUsdvBalanceForAddress(${address}): no USDv mint match in ${accounts.length} token accounts`,
            accounts.map((a) => a.mint)
        );
    }
    return usdv;
}

/**
 * Fetches the USDv balance for an arbitrary Solana address.
 *
 * Unlike `useTokenBalancesData`, this is not tied to the connected wallet —
 * pass any address (typically a position's Source Account wallet).
 * Each unique address is cached independently. Refresh while visible and on
 * return from external wallet apps so their transfers update the live balance.
 */
export function useUsdvBalanceForAddress(address: string | null | undefined): UseUsdvBalanceResult {
    const { data, isLoading, isFetching, isSuccess, error, refetch } = useQuery<BigDecimal | null, Error>({
        queryKey: usdvBalanceQueryKey(address ?? ""),
        queryFn: () => fetchUsdvBalanceForAddress(address as string),
        enabled: Boolean(address),
        staleTime: USDV_BALANCE_STALE_TIME_MS,
        refetchInterval: USDV_BALANCE_STALE_TIME_MS,
        refetchOnWindowFocus: "always",
        gcTime: USDV_BALANCE_GC_TIME_MS
    });

    return {
        amount: data ?? null,
        isLoading,
        isFetching,
        hasFetched: isSuccess,
        error: error ?? null,
        refetch: (): void => {
            void refetch();
        }
    };
}

export interface UseTokenBalancesForAddressResult {
    balances: TokenBalances;
    isLoading: boolean;
    isFetching: boolean;
    hasFetched: boolean;
    error: Error | null;
    refetch: () => void;
}

const EMPTY_BALANCES: TokenBalances = {
    usdv: null,
    legacyUsdv: null,
    usdc: null,
    usdt: null,
    susdv: null,
    solo: null
};

/**
 * Every tracked token balance for an arbitrary address (e.g. a Squads vault),
 * with the same refresh policy as `useUsdvBalanceForAddress`.
 */
export function useTokenBalancesForAddress(address: string | null | undefined): UseTokenBalancesForAddressResult {
    const { data, isLoading, isFetching, error, refetch } = useQuery<TokenBalances, Error>({
        queryKey: tokenBalancesQueryKey(address ?? ""),
        queryFn: () => fetchTokenBalancesForAddress(address as string),
        enabled: Boolean(address),
        staleTime: USDV_BALANCE_STALE_TIME_MS,
        refetchInterval: USDV_BALANCE_STALE_TIME_MS,
        refetchOnWindowFocus: "always",
        gcTime: USDV_BALANCE_GC_TIME_MS
    });
    return {
        balances: data ?? EMPTY_BALANCES,
        isLoading,
        isFetching,
        // Preserve the last successful balances if a background refresh fails.
        hasFetched: data !== undefined,
        error,
        refetch: (): void => {
            void refetch();
        }
    };
}
